import {type HgHeroCTAProps} from '~/design-system/hg/modules'
import {getEmailCaptureDestinationProps} from '~/cms/helpers/getEmailCaptureProps/getEmailCaptureDestinationProps'
import {getLinkProps} from '~/cms/components/LinkComponent'
import {
  type Node,
  type Root,
  type StructuredText,
  isDocument,
} from 'datocms-structured-text-utils'
import {
  type PartnerPageLogo,
  type HeroSectionProps,
  type LogoAssetLogo,
} from '../_types'
import {type CSSProperties} from 'react'
import LogoSVG from '~/cms/modules/_FEM/LogoMash/LogoSVG'
import {Image as ReactDatoImage} from 'react-datocms'

export function getCTATypeProps(data: HeroSectionProps['data']): HgHeroCTAProps {
  if (data.ctaType === 'email_capture' && data.emailCapture) {
    return {
      contentType: 'emailCapture',
      emailCaptureProps: getEmailCaptureDestinationProps(data.emailCapture),
    }
  }

  return {
    contentType: 'buttonGroup',
    primaryButtonProps: getLinkProps(data.primaryButton),
    secondaryButtonProps: getLinkProps(data.secondaryButton),
  }
}

export function structuredTextCharacterCount(structuredText: StructuredText) {
  let totalCharacters = 0

  function countCharacters(node: Node | Root) {
    if ('value' in node && node.value) {
      totalCharacters += node.value.length
    }

    if ('children' in node && node.children) {
      node.children.forEach(child => {
        countCharacters(child)
      })
    }
  }

  if (isDocument(structuredText.value)) {
    countCharacters(structuredText.value.document)
  }

  return totalCharacters
}

export function getLogo(logoData?: PartnerPageLogo) {
  if (!logoData) {
    return
  }

  if (typeof logoData === 'string') {
    return logoData
  }

  if (logoData.logo.format === 'svg') {
    return (
      <div
        style={
          {
            '--height': `${typeof logoData.height === 'number' ? logoData.height : 24}px`,
          } as CSSProperties
        }
        className="[&_svg]:h-[var(--height)] [&_svg]:w-full"
      >
        <LogoSVG logo={logoData} />
      </div>
    )
  }

  if (!logoData.logo.logoResponsiveImage) {
    return
  }

  return (
    <ReactDatoImage
      priority
      data={{...logoData.logo.logoResponsiveImage, alt: logoData.companyName}}
      imgStyle={{height: logoData.height ?? '24px', width: 'auto'}}
    />
  )
}

export function getLogoFromLogoAsset(logoData?: LogoAssetLogo) {
  if (!logoData) {
    return
  }

  if (typeof logoData === 'string') {
    return logoData
  }

  if (logoData.logo.logo.format === 'svg') {
    const height: number = logoData.logo.logo.height ?? 24
    return (
      <div
        style={
          {
            '--height': `${height}px`,
          } as CSSProperties
        }
        className="[&_svg]:h-[var(--height)] [&_svg]:w-full"
      >
        <LogoSVG
          logo={{
            id: logoData.id,
            companyName: logoData.logo.companyName,
            logo: {
              id: logoData.logo.logo.id,
              url: logoData.logo.logo.url,
              width: logoData.logo.logo.width,
              height: logoData.logo.logo.height,
            },
          }}
        />
      </div>
    )
  }

  if (!logoData.logo.logo.logoResponsiveImage) {
    return
  }

  return (
    <ReactDatoImage
      priority
      data={{
        ...logoData.logo.logo.logoResponsiveImage,
        alt: logoData.logo.companyName,
      }}
    />
  )
}
